import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  FolderOpen,
  HelpCircle,
  Loader2,
  Play,
  Plus,
  Save,
  Sparkles,
  Stethoscope,
  Trash2,
  UserRound,
  ClipboardList,
  CalendarCheck,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import { downloadCsv } from "@/lib/exportCsv";

const PAGE_SIZE = 25;
const NO_VALUE_OPS = ["is_set", "is_empty"];
const LIST_OPS = ["in", "between"];

const STEPS = [
  { id: 1, label: "Data source", help: "Choose which part of the business you want to report on." },
  { id: 2, label: "Filters", help: "Narrow the records down. Leave empty to pull everything." },
  { id: 3, label: "Columns", help: "Tick the information you want to see in each row." },
  { id: 4, label: "Run", help: "Run the report, export it to CSV or save it as a SmartView." },
];

const SOURCE_META = {
  pipeline_cases: { icon: ClipboardList, blurb: "Referrals moving through the SS and TT pipelines" },
  visits: { icon: Stethoscope, blurb: "Every doctor visit and appointment captured by reps" },
  doctors: { icon: UserRound, blurb: "Your doctor base, practices and territories" },
  referral_pads: { icon: FileText, blurb: "Pads issued, slips used and slips remaining" },
  rep_activity: { icon: CalendarCheck, blurb: "Daily and month-to-date totals per rep" },
};

const DEFAULT_COLUMNS = {
  pipeline_cases: ["tin_number", "name", "surname", "current_stage", "assigned_rep_name"],
  visits: ["rep_name", "doctor_name", "area", "date_of_visit", "visit_type"],
  doctors: ["doctor_name", "mp_number", "practice_name", "area", "assigned_rep"],
  referral_pads: ["doctor_name", "issued_date", "referrals_remaining", "status", "issued_by_rep"],
  rep_activity: ["rep_name", "activity_date", "daily_visits", "daily_referrals", "monthly_referrals"],
};

const EXAMPLE_FILTERS = {
  pipeline_cases: [
    { label: "Pipeline is SS", field: "current_pipeline", operator: "equals", value: "SS" },
    { label: "Area contains Menlyn", field: "area", operator: "contains", value: "Menlyn" },
    { label: "Stage contains Feedback", field: "current_stage", operator: "contains", value: "Feedback" },
  ],
  visits: [
    { label: "Visit type contains Cold", field: "visit_type", operator: "contains", value: "Cold" },
    { label: "Area contains Pretoria", field: "area", operator: "contains", value: "Pretoria" },
    { label: "Visit date on or after 2026-06-01", field: "date_of_visit", operator: ">=", value: "2026-06-01" },
  ],
  doctors: [
    { label: "Area contains Menlyn", field: "area", operator: "contains", value: "Menlyn" },
    { label: "MP number has any value", field: "mp_number", operator: "is_set", value: "" },
    { label: "Status is Active", field: "status", operator: "equals", value: "Active" },
  ],
  referral_pads: [
    { label: "Slips remaining less than 3", field: "referrals_remaining", operator: "<", value: "3" },
    { label: "Status is Active", field: "status", operator: "equals", value: "Active" },
    { label: "Issued date on or after 2026-06-01", field: "issued_date", operator: ">=", value: "2026-06-01" },
  ],
  rep_activity: [
    { label: "Visits that day more than 3", field: "daily_visits", operator: ">", value: "3" },
    { label: "Referrals that day at least 1", field: "daily_referrals", operator: ">=", value: "1" },
    { label: "Rep contains Anel", field: "rep_name", operator: "contains", value: "Anel" },
  ],
};

const ASSISTANT_EXAMPLES = {
  pipeline_cases: [
    "Show all SS cases in Menlyn",
    "TT cases by Anel referred this month",
    "Lost cases in Pretoria",
  ],
  visits: ["All visits by Anel this month", "Visits in Menlyn area", "Doctor visits in September"],
  doctors: ["All doctors in Menlyn", "Doctors with status active", "Doctors in Pretoria by Anel"],
  referral_pads: ["Active pads with less than 3 slips remaining", "Pads issued this month", "Depleted pads by Anel"],
  rep_activity: ["Rep activity for Anel this week", "Reps with more than 5 visits today", "Daily referrals last 7 days"],
};

const OP_LABELS = {
  string: {
    equals: "is",
    not_equals: "is not",
    contains: "contains",
    starts_with: "starts with",
    in: "is any of",
    is_set: "has any value",
    is_empty: "is blank",
  },
  date: {
    equals: "is on",
    ">": "is after",
    "<": "is before",
    ">=": "on or after",
    "<=": "on or before",
    between: "is between",
    is_set: "has any value",
    is_empty: "is blank",
  },
  number: {
    equals: "equals",
    ">": "more than",
    "<": "less than",
    ">=": "at least",
    "<=": "at most",
    between: "between",
    is_set: "has any value",
    is_empty: "is blank",
  },
};

const opLabel = (operator, type) => OP_LABELS[type]?.[operator] || operator;

const newCondition = (patch = {}) => ({
  key: `${Date.now()}-${Math.random()}`,
  field: "",
  operator: "",
  value: "",
  ...patch,
});

const toApiValue = (condition, fieldMeta) => {
  if (NO_VALUE_OPS.includes(condition.operator)) return null;
  const raw = condition.value;
  if (LIST_OPS.includes(condition.operator)) {
    const parts = String(raw)
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    return fieldMeta?.type === "number" ? parts.map(Number) : parts;
  }
  return fieldMeta?.type === "number" ? Number(raw) : raw;
};

const Help = ({ text, testId }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button type="button" className="text-muted-foreground hover:text-foreground" data-testid={testId}>
        <HelpCircle className="h-4 w-4" />
      </button>
    </TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs">{text}</TooltipContent>
  </Tooltip>
);

export default function RecordExplorer() {
  const [schema, setSchema] = useState(null);
  const [step, setStep] = useState(1);
  const [collection, setCollection] = useState("pipeline_cases");
  const [logic, setLogic] = useState("AND");
  const [conditions, setConditions] = useState([newCondition()]);
  const [group, setGroup] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [columns, setColumns] = useState([]);
  const [sort, setSort] = useState({ column: null, dir: "asc" });
  const [page, setPage] = useState(0);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [views, setViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [loadOpen, setLoadOpen] = useState(false);
  const [viewName, setViewName] = useState("");
  const [viewDescription, setViewDescription] = useState("");
  const [editingViewId, setEditingViewId] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantQuery, setAssistantQuery] = useState("");
  const [assistantResult, setAssistantResult] = useState(null);
  const [assistantLoading, setAssistantLoading] = useState(false);

  const fetchViews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/smart-views`, { withCredentials: true });
      setViews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("SmartViews load failed", error);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/smart-reports/schema`, { withCredentials: true })
      .then(({ data }) => {
        setSchema(data);
        setColumns(DEFAULT_COLUMNS.pipeline_cases);
      })
      .catch((error) => {
        console.error("Schema load failed", error);
        toast.error("Failed to load the report schema");
      });
    fetchViews();
  }, [fetchViews]);

  const collectionMeta = schema?.collections?.[collection];
  const fields = collectionMeta?.fields || [];
  const fieldByName = useMemo(() => Object.fromEntries(fields.map((f) => [f.name, f])), [fields]);

  const joinColumns = useMemo(() => {
    if (!schema) return [];
    return (collectionMeta?.joins || []).flatMap((join) =>
      (schema.joined_columns?.[join] || []).map((key) => `${join}.${key}`)
    );
  }, [schema, collectionMeta]);

  const buildFilterGroup = useCallback(() => {
    const mapConditions = (list) =>
      list
        .filter((c) => c.field && c.operator && (NO_VALUE_OPS.includes(c.operator) || String(c.value).trim() !== ""))
        .map((c) => ({ field: c.field, operator: c.operator, value: toApiValue(c, fieldByName[c.field]) }));

    const payload = { logic, conditions: mapConditions(conditions), groups: [] };
    if (group) {
      const nested = mapConditions(group.conditions);
      if (nested.length) payload.groups = [{ logic: group.logic, conditions: nested, groups: [] }];
    }
    return payload;
  }, [logic, conditions, group, fieldByName]);

  const defaultColumnsFor = (name) =>
    (DEFAULT_COLUMNS[name] || (schema?.collections?.[name]?.fields || []).slice(0, 5).map((f) => f.name)).filter(
      (col) => (schema?.collections?.[name]?.fields || []).some((f) => f.name === col)
    );

  const changeCollection = (next) => {
    setCollection(next);
    setConditions([newCondition()]);
    setGroup(null);
    setShowAdvanced(false);
    setResults(null);
    setSort({ column: null, dir: "asc" });
    setPage(0);
    setColumns(defaultColumnsFor(next));
    setAssistantResult(null);
  };

  const updateCondition = (key, patch, inGroup = false) => {
    const updater = (list) => list.map((c) => (c.key === key ? { ...c, ...patch } : c));
    if (inGroup) setGroup((prev) => ({ ...prev, conditions: updater(prev.conditions) }));
    else setConditions(updater);
  };

  const removeCondition = (key, inGroup = false) => {
    if (inGroup) setGroup((prev) => ({ ...prev, conditions: prev.conditions.filter((c) => c.key !== key) }));
    else setConditions((prev) => (prev.length === 1 ? [newCondition()] : prev.filter((c) => c.key !== key)));
  };

  const applyExample = (example) => {
    const condition = newCondition({ field: example.field, operator: example.operator, value: example.value });
    setConditions((prev) => {
      const empty = prev.filter((c) => c.field || c.operator);
      return [...empty, condition];
    });
    toast.success(`Added: ${example.label}`);
  };

  const runQuery = async () => {
    if (columns.length === 0) {
      toast.error("Select at least one column");
      setStep(3);
      return;
    }
    const incomplete = [...conditions, ...(group?.conditions || [])].some(
      (c) =>
        (c.field || c.operator) &&
        !(c.field && c.operator && (NO_VALUE_OPS.includes(c.operator) || String(c.value).trim() !== ""))
    );
    if (incomplete) toast.warning("Incomplete filters were ignored — finish them to include them");

    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/smart-reports/query`,
        { primary_collection: collection, filter_group: buildFilterGroup(), selected_columns: columns, limit: 1000 },
        { withCredentials: true }
      );
      setResults(data);
      setPage(0);
      setStep(4);
      toast.success(`${data.total_records} record(s) found`);
    } catch (error) {
      console.error("Query failed", error);
      toast.error(error?.response?.data?.detail || "Failed to run the query");
    } finally {
      setLoading(false);
    }
  };

  const askAssistant = async () => {
    if (!assistantQuery.trim()) {
      toast.error("Describe what you want to see");
      return;
    }
    setAssistantLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/smart-reports/parse-query`,
        { user_query: assistantQuery, collection },
        { withCredentials: true }
      );
      setAssistantResult(data);
      if (!data.suggested_filters?.length) toast.info("Nothing recognised — try one of the examples");
    } catch (error) {
      console.error("Assistant failed", error);
      toast.error("Could not read that request");
    } finally {
      setAssistantLoading(false);
    }
  };

  const applyAssistant = () => {
    const suggestions = assistantResult?.suggested_filters || [];
    if (!suggestions.length) return;
    setConditions(
      suggestions.map((s) =>
        newCondition({
          field: s.field,
          operator: s.operator,
          value: Array.isArray(s.value) ? s.value.join(", ") : s.value === null ? "" : String(s.value),
        })
      )
    );
    setLogic(assistantResult.suggested_logic || "AND");
    if (assistantResult.suggested_columns?.length) setColumns(assistantResult.suggested_columns);
    setAssistantOpen(false);
    setAssistantResult(null);
    setAssistantQuery("");
    setStep(2);
    toast.success("Filters applied — review them and run the report");
  };

  const saveView = async () => {
    if (!viewName.trim()) {
      toast.error("Give the view a name");
      return;
    }
    const payload = {
      view_name: viewName.trim(),
      description: viewDescription.trim() || null,
      primary_collection: collection,
      filter_group: buildFilterGroup(),
      selected_columns: columns,
      sort_by: sort.column,
      sort_dir: sort.dir,
    };
    try {
      if (editingViewId) {
        await axios.put(`${API}/smart-views/${editingViewId}`, payload, { withCredentials: true });
        toast.success("SmartView updated");
      } else {
        await axios.post(`${API}/smart-views`, payload, { withCredentials: true });
        toast.success("SmartView saved");
      }
      setSaveOpen(false);
      setViewName("");
      setViewDescription("");
      fetchViews();
    } catch (error) {
      console.error("Save failed", error);
      toast.error(error?.response?.data?.detail || "Failed to save the SmartView");
    }
  };

  const loadView = (view) => {
    setCollection(view.primary_collection);
    setColumns(view.selected_columns || []);
    setLogic(view.filter_group?.logic || "AND");
    setConditions(
      (view.filter_group?.conditions || []).map((c) =>
        newCondition({
          field: c.field,
          operator: c.operator,
          value: Array.isArray(c.value) ? c.value.join(", ") : c.value ?? "",
        })
      )
    );
    const nested = view.filter_group?.groups?.[0];
    setGroup(
      nested
        ? {
            logic: nested.logic || "OR",
            conditions: (nested.conditions || []).map((c) =>
              newCondition({
                field: c.field,
                operator: c.operator,
                value: Array.isArray(c.value) ? c.value.join(", ") : c.value ?? "",
              })
            ),
          }
        : null
    );
    setShowAdvanced(Boolean(nested));
    setSort({ column: view.sort_by || null, dir: view.sort_dir || "asc" });
    setEditingViewId(view.view_id);
    setViewName(view.view_name);
    setViewDescription(view.description || "");
    setLoadOpen(false);
    setResults(null);
    setStep(4);
    toast.success(`Loaded "${view.view_name}"`);
  };

  const deleteView = async (viewId) => {
    try {
      await axios.delete(`${API}/smart-views/${viewId}`, { withCredentials: true });
      if (editingViewId === viewId) setEditingViewId(null);
      toast.success("SmartView deleted");
      fetchViews();
    } catch (error) {
      console.error("Delete failed", error);
      toast.error("Failed to delete the SmartView");
    }
  };

  const resultColumns = results?.columns || columns.map((c) => c.replace(".", "_"));
  const sortedRows = useMemo(() => {
    const rows = [...(results?.data || [])];
    if (!sort.column) return rows;
    const key = sort.column.replace(".", "_");
    return rows.sort((a, b) => {
      const av = a[key] ?? "";
      const bv = b[key] ?? "";
      if (av === bv) return 0;
      return (av > bv ? 1 : -1) * (sort.dir === "desc" ? -1 : 1);
    });
  }, [results, sort]);

  const pageRows = sortedRows.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));

  const exportCsv = () =>
    downloadCsv(
      `smart-report-${collection}-${new Date().toISOString().split("T")[0]}`,
      resultColumns,
      sortedRows.map((row) =>
        resultColumns.map((col) => (Array.isArray(row[col]) ? row[col].join(" | ") : row[col] ?? ""))
      )
    );

  const toggleSort = (column) =>
    setSort((prev) => ({ column, dir: prev.column === column && prev.dir === "asc" ? "desc" : "asc" }));

  const activeConditions = [...conditions, ...(group?.conditions || [])].filter((c) => c.field && c.operator);

  const renderCondition = (condition, inGroup = false) => {
    const meta = fieldByName[condition.field];
    const needsValue = condition.operator && !NO_VALUE_OPS.includes(condition.operator);
    return (
      <div
        key={condition.key}
        className="rounded-lg border bg-muted/30 p-3 space-y-2"
        data-testid={`filter-row-${condition.key}`}
      >
        <div className="flex items-start gap-2">
          <select
            value={condition.field}
            onChange={(e) => updateCondition(condition.key, { field: e.target.value, operator: "", value: "" }, inGroup)}
            className="flex-1 min-w-0 h-10 px-2 rounded-md border border-input bg-background text-sm"
            data-testid="filter-field-select"
          >
            <option value="">Select field…</option>
            {fields.map((f) => (
              <option key={f.name} value={f.name}>
                {f.label || f.name}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removeCondition(condition.key, inGroup)}
            data-testid="filter-remove-btn"
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>

        {condition.field && (
          <div className="flex flex-wrap gap-1.5" data-testid="filter-operator-group">
            {(meta?.operators || []).map((op) => (
              <button
                key={op}
                type="button"
                onClick={() => updateCondition(condition.key, { operator: op, value: "" }, inGroup)}
                className={`px-2.5 py-1 rounded-full border text-xs transition-colors ${
                  condition.operator === op
                    ? "bg-blue-600 border-blue-600 text-white"
                    : "bg-background border-input hover:bg-muted"
                }`}
                data-testid={`filter-operator-${op}`}
              >
                {opLabel(op, meta?.type)}
              </button>
            ))}
          </div>
        )}

        {needsValue && (
          <Input
            type={meta?.type === "date" && !LIST_OPS.includes(condition.operator) ? "date" : "text"}
            placeholder={
              LIST_OPS.includes(condition.operator)
                ? condition.operator === "between"
                  ? "from, to"
                  : "value, value, value"
                : "Value"
            }
            value={condition.value}
            onChange={(e) => updateCondition(condition.key, { value: e.target.value }, inGroup)}
            data-testid="filter-value-input"
          />
        )}
      </div>
    );
  };

  if (!schema) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="smart-reports-loading">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900" data-testid="record-explorer-panel">
        <div className="px-4 sm:px-6 pt-6 pb-4">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-bold tracking-tight">Record Explorer</h1>
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => setAssistantOpen(true)}
              data-testid="open-assistant-btn"
            >
              <Sparkles className="h-4 w-4 text-blue-600" />
              Report Assistant
            </Button>
            <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => setLoadOpen(true)} data-testid="load-view-btn">
              <FolderOpen className="h-4 w-4" />
              SmartViews ({views.length})
            </Button>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Build your own reports across referrals, visits, doctors, pads and rep activity — four quick steps.
          </p>
        </div>

        {/* Stepper */}
        <div className="px-4 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-1" data-testid="wizard-stepper">
            {STEPS.map((s, index) => (
              <React.Fragment key={s.id}>
                <button
                  onClick={() => setStep(s.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors w-full sm:w-auto ${
                    step === s.id
                      ? "bg-blue-600 text-white"
                      : "bg-white dark:bg-slate-800 border hover:bg-muted text-foreground"
                  }`}
                  data-testid={`step-btn-${s.id}`}
                >
                  <span
                    className={`h-5 w-5 shrink-0 rounded-full grid place-items-center text-xs font-semibold ${
                      step === s.id ? "bg-white/20" : "bg-muted"
                    }`}
                  >
                    {s.id}
                  </span>
                  {s.label}
                </button>
                {index < STEPS.length - 1 && (
                  <ChevronRight className="hidden sm:block h-4 w-4 text-muted-foreground shrink-0" />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="px-4 sm:px-6 py-5 space-y-6">
          {/* STEP 1 */}
          {step === 1 && (
            <Card data-testid="step-1-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Step 1 · Pick your data source</CardTitle>
                  <Help text={STEPS[0].help} testId="help-step-1" />
                </div>
                <CardDescription>{STEPS[0].help}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                  {Object.entries(schema.collections).map(([id, meta]) => {
                    const Icon = SOURCE_META[id]?.icon || FileText;
                    const active = collection === id;
                    return (
                      <button
                        key={id}
                        onClick={() => changeCollection(id)}
                        className={`text-left p-4 rounded-xl border-2 transition-all ${
                          active
                            ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40"
                            : "border-border bg-white dark:bg-slate-800 hover:border-blue-300"
                        }`}
                        data-testid={`source-${id}`}
                      >
                        <Icon className={`h-5 w-5 mb-2 ${active ? "text-blue-600" : "text-muted-foreground"}`} />
                        <p className="font-semibold text-sm">{meta.label || id}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{SOURCE_META[id]?.blurb}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="flex justify-end">
                  <Button className="gap-1" onClick={() => setStep(2)} data-testid="step-1-next-btn">
                    Next: filters
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <Card data-testid="step-2-card">
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">Step 2 · Add filters</CardTitle>
                  <Help text={STEPS[1].help} testId="help-step-2" />
                  <Badge variant="secondary" data-testid="active-filter-count">
                    {activeConditions.length} active
                  </Badge>
                </div>
                <CardDescription>
                  Reporting on <span className="font-medium">{collectionMeta?.label || collection}</span>. Leave the
                  filters empty to pull every record.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* AND/OR toggle */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5">
                    <Label className="text-xs uppercase tracking-wide text-muted-foreground">Match</Label>
                    <Help
                      text="AND = a record must satisfy every filter. OR = a record only needs to satisfy one of them."
                      testId="help-logic"
                    />
                  </div>
                  <div className="inline-flex rounded-lg border p-0.5 bg-background" data-testid="logic-toggle">
                    {["AND", "OR"].map((option) => (
                      <button
                        key={option}
                        onClick={() => setLogic(option)}
                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                          logic === option ? "bg-blue-600 text-white" : "text-muted-foreground hover:text-foreground"
                        }`}
                        data-testid={`logic-${option.toLowerCase()}-btn`}
                      >
                        {option === "AND" ? "Match all (AND)" : "Match any (OR)"}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Examples */}
                <div className="rounded-lg border bg-blue-50/60 dark:bg-blue-950/30 p-3">
                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mb-2">Try one of these</p>
                  <div className="flex flex-wrap gap-2">
                    {(EXAMPLE_FILTERS[collection] || []).map((example) => (
                      <button
                        key={example.label}
                        onClick={() => applyExample(example)}
                        className="px-2.5 py-1 rounded-full bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 text-xs hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors"
                        data-testid={`example-filter-${example.field}`}
                      >
                        + {example.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">{conditions.map((condition) => renderCondition(condition))}</div>

                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1"
                  onClick={() => setConditions((p) => [...p, newCondition()])}
                  data-testid="add-filter-btn"
                >
                  <Plus className="h-4 w-4" />
                  Add filter
                </Button>

                {/* Advanced filters */}
                <div className="border-t pt-4">
                  <button
                    onClick={() => setShowAdvanced((v) => !v)}
                    className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                    data-testid="advanced-filters-toggle"
                  >
                    {showAdvanced ? "▾" : "▸"} Advanced filters
                    <span className="font-normal">(nested groups)</span>
                  </button>

                  {showAdvanced && (
                    <div className="mt-3 space-y-3" data-testid="advanced-filters-panel">
                      {!group ? (
                        <>
                          <p className="text-xs text-muted-foreground">
                            A group lets you say things like “area is Menlyn <b>AND</b> (stage is Feedback{" "}
                            <b>OR</b> stage is Reports)”.
                          </p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() => setGroup({ logic: "OR", conditions: [newCondition()] })}
                            data-testid="add-group-btn"
                          >
                            <Plus className="h-4 w-4" />
                            Add group
                          </Button>
                        </>
                      ) : (
                        <div className="rounded-lg border bg-muted/40 p-4 space-y-3" data-testid="filter-group">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-wide">{logic} group where</span>
                              <div className="inline-flex rounded-lg border p-0.5 bg-background" data-testid="group-logic-toggle">
                                {["OR", "AND"].map((option) => (
                                  <button
                                    key={option}
                                    onClick={() => setGroup((prev) => ({ ...prev, logic: option }))}
                                    className={`px-2.5 py-1 text-xs font-semibold rounded-md ${
                                      group.logic === option ? "bg-blue-600 text-white" : "text-muted-foreground"
                                    }`}
                                    data-testid={`group-logic-${option.toLowerCase()}-btn`}
                                  >
                                    {option === "OR" ? "any matches" : "all match"}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <Button variant="ghost" size="sm" onClick={() => setGroup(null)} data-testid="remove-group-btn">
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                          {group.conditions.map((condition) => renderCondition(condition, true))}
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1"
                            onClick={() =>
                              setGroup((prev) => ({ ...prev, conditions: [...prev.conditions, newCondition()] }))
                            }
                            data-testid="add-group-filter-btn"
                          >
                            <Plus className="h-4 w-4" />
                            Add to group
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-between gap-2 border-t pt-4">
                  <Button variant="outline" className="gap-1" onClick={() => setStep(1)} data-testid="step-2-back-btn">
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <Button className="gap-1" onClick={() => setStep(3)} data-testid="step-2-next-btn">
                    Next: columns
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 3 */}
          {step === 3 && (
            <Card data-testid="step-3-card">
              <CardHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <CardTitle className="text-base">Step 3 · Choose your columns</CardTitle>
                  <Help text={STEPS[2].help} testId="help-step-3" />
                  <Badge variant="secondary" data-testid="column-count">
                    {columns.length} selected
                  </Badge>
                </div>
                <CardDescription>
                  We pre-select the five most useful columns. Linked columns pull extra detail from related records.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setColumns(defaultColumnsFor(collection))}
                    data-testid="reset-columns-btn"
                  >
                    Reset to suggested 5
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setColumns([])} data-testid="clear-columns-btn">
                    Clear all
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                  {fields.map((field) => (
                    <label
                      key={field.name}
                      className="flex items-center gap-2 text-sm cursor-pointer py-1 rounded hover:bg-muted px-1"
                    >
                      <input
                        type="checkbox"
                        checked={columns.includes(field.name)}
                        onChange={(e) =>
                          setColumns((prev) =>
                            e.target.checked ? [...prev, field.name] : prev.filter((c) => c !== field.name)
                          )
                        }
                        data-testid={`column-toggle-${field.name}`}
                      />
                      <span className="truncate">{field.label || field.name}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{field.type}</span>
                    </label>
                  ))}
                </div>

                {joinColumns.length > 0 && (
                  <div className="border-t pt-4">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        From linked records
                      </p>
                      <Help
                        text="These pull values from related collections, e.g. a doctor's MP number onto a referral row."
                        testId="help-linked-columns"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1">
                      {joinColumns.map((column) => (
                        <label
                          key={column}
                          className="flex items-center gap-2 text-sm cursor-pointer py-1 rounded hover:bg-muted px-1"
                        >
                          <input
                            type="checkbox"
                            checked={columns.includes(column)}
                            onChange={(e) =>
                              setColumns((prev) =>
                                e.target.checked ? [...prev, column] : prev.filter((c) => c !== column)
                              )
                            }
                            data-testid={`column-toggle-${column}`}
                          />
                          <span className="truncate">{column}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-between gap-2 border-t pt-4">
                  <Button variant="outline" className="gap-1" onClick={() => setStep(2)} data-testid="step-3-back-btn">
                    <ChevronLeft className="h-4 w-4" />
                    Back
                  </Button>
                  <Button className="gap-1" onClick={() => setStep(4)} data-testid="step-3-next-btn">
                    Next: run
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 4 */}
          {step === 4 && (
            <Card data-testid="step-4-card">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Step 4 · Run your report</CardTitle>
                  <Help text={STEPS[3].help} testId="help-step-4" />
                </div>
                <CardDescription data-testid="query-summary">
                  {collectionMeta?.label || collection} · {activeConditions.length} filter(s) matched with{" "}
                  {logic === "AND" ? "all" : "any"} · {columns.length} column(s)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={runQuery} disabled={loading} className="gap-2 sm:flex-1" data-testid="run-query-btn">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    {loading ? "Running…" : "Run query"}
                  </Button>
                  <Button variant="outline" className="gap-2" onClick={() => setSaveOpen(true)} data-testid="save-view-btn">
                    <Save className="h-4 w-4" />
                    {editingViewId ? "Update SmartView" : "Save as SmartView"}
                  </Button>
                  {results?.data?.length > 0 && (
                    <Button variant="outline" className="gap-2" onClick={exportCsv} data-testid="export-csv-btn">
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                  )}
                </div>
                {results && (
                  <Badge variant="secondary" className="py-1.5" data-testid="record-count">
                    {results.total_records} record(s) matched
                  </Badge>
                )}
                <Button variant="outline" className="gap-1" onClick={() => setStep(3)} data-testid="step-4-back-btn">
                  <ChevronLeft className="h-4 w-4" />
                  Back to columns
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {results && (
            <Card data-testid="results-card">
              <CardHeader>
                <CardTitle className="text-base">Results ({results.total_records})</CardTitle>
                <CardDescription>
                  {results.truncated ? "Showing the first 1000 matching records" : "All matching records"} · tap a
                  column heading to sort
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sortedRows.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No records match these filters.</p>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            {resultColumns.map((column) => (
                              <th key={column} className="text-left py-2 px-3 font-semibold whitespace-nowrap">
                                <button
                                  className="inline-flex items-center gap-1 hover:text-blue-600"
                                  onClick={() => toggleSort(column)}
                                  data-testid={`sort-${column}`}
                                >
                                  {column}
                                  {sort.column === column &&
                                    (sort.dir === "asc" ? (
                                      <ArrowUp className="h-3 w-3" />
                                    ) : (
                                      <ArrowDown className="h-3 w-3" />
                                    ))}
                                </button>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pageRows.map((row, index) => (
                            <tr
                              key={index}
                              className="border-b hover:bg-muted/50"
                              data-testid={`result-row-${index}`}
                            >
                              {resultColumns.map((column) => (
                                <td key={column} className="py-2 px-3 whitespace-nowrap">
                                  {Array.isArray(row[column])
                                    ? row[column].join(", ") || "—"
                                    : row[column] === null || row[column] === undefined || row[column] === ""
                                    ? "—"
                                    : String(row[column])}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-4">
                      <p className="text-xs text-muted-foreground">
                        Page {page + 1} of {pageCount} · {sortedRows.length} rows
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page === 0}
                          onClick={() => setPage((p) => p - 1)}
                          data-testid="page-prev-btn"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page + 1 >= pageCount}
                          onClick={() => setPage((p) => p + 1)}
                          data-testid="page-next-btn"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Report Assistant */}
        <Dialog open={assistantOpen} onOpenChange={setAssistantOpen}>
          <DialogContent
            className="max-w-lg bg-white dark:bg-slate-900 max-h-[90vh] overflow-y-auto"
            data-testid="assistant-modal"
          >
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-blue-600" />
                Report Assistant
              </DialogTitle>
              <DialogDescription>
                Describe the report in your own words and we will set up the filters for you.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">What do you want to see?</Label>
                <Textarea
                  rows={3}
                  value={assistantQuery}
                  onChange={(e) => setAssistantQuery(e.target.value)}
                  placeholder="e.g. Show all SS cases in Menlyn by Anel referred this month"
                  className="mt-1"
                  data-testid="assistant-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Reading from: {collectionMeta?.label || collection}
                </p>
              </div>

              <div>
                <p className="text-xs font-semibold text-muted-foreground mb-1.5">Try:</p>
                <div className="flex flex-wrap gap-2">
                  {(ASSISTANT_EXAMPLES[collection] || []).map((example) => (
                    <button
                      key={example}
                      onClick={() => setAssistantQuery(example)}
                      className="px-2.5 py-1 rounded-full border text-xs hover:bg-muted"
                      data-testid="assistant-example"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>

              <Button onClick={askAssistant} disabled={assistantLoading} className="w-full gap-2" data-testid="assistant-suggest-btn">
                {assistantLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                Suggest filters
              </Button>

              {assistantResult && (
                <div className="space-y-3" data-testid="assistant-suggestions">
                  {assistantResult.suggested_filters?.length > 0 ? (
                    <div className="rounded-lg border bg-blue-50/60 dark:bg-blue-950/30 p-3 space-y-1">
                      <p className="text-xs font-semibold mb-1">Suggested filters</p>
                      {assistantResult.suggested_filters.map((suggestion, index) => {
                        const meta = fieldByName[suggestion.field];
                        return (
                          <p key={index} className="text-xs">
                            • <span className="font-medium">{meta?.label || suggestion.field}</span>{" "}
                            {opLabel(suggestion.operator, meta?.type)}{" "}
                            <span className="font-medium">
                              {Array.isArray(suggestion.value) ? suggestion.value.join(" → ") : String(suggestion.value ?? "")}
                            </span>
                          </p>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Nothing recognised in that sentence — try one of the examples above.
                    </p>
                  )}

                  {assistantResult.notes?.map((note, index) => (
                    <p key={index} className="text-xs text-amber-600 dark:text-amber-400" data-testid="assistant-note">
                      {note}
                    </p>
                  ))}

                  {assistantResult.suggested_filters?.length > 0 && (
                    <Button onClick={applyAssistant} className="w-full" data-testid="assistant-apply-btn">
                      Apply these filters
                    </Button>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Save SmartView */}
        <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
          <DialogContent className="bg-white dark:bg-slate-900" data-testid="save-view-modal">
            <DialogHeader>
              <DialogTitle>{editingViewId ? "Update SmartView" : "Save SmartView"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={viewName}
                  onChange={(e) => setViewName(e.target.value)}
                  placeholder="e.g. PTA sleep studies waiting on authorisation"
                  className="mt-1"
                  data-testid="save-view-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={viewDescription}
                  onChange={(e) => setViewDescription(e.target.value)}
                  placeholder="What is this report for?"
                  className="mt-1"
                  data-testid="save-view-description-input"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                {editingViewId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingViewId(null);
                      toast.info("Saving as a new SmartView");
                    }}
                    data-testid="save-as-new-btn"
                  >
                    Save as new instead
                  </Button>
                )}
                <Button onClick={saveView} data-testid="save-view-confirm-btn">
                  {editingViewId ? "Update" : "Save"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Load SmartView */}
        <Dialog open={loadOpen} onOpenChange={setLoadOpen}>
          <DialogContent className="max-w-lg bg-white dark:bg-slate-900" data-testid="load-view-modal">
            <DialogHeader>
              <DialogTitle>Your SmartViews</DialogTitle>
            </DialogHeader>
            {views.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nothing saved yet — build a query and save it as a SmartView.
              </p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {views.map((view) => (
                  <div
                    key={view.view_id}
                    className="flex items-center justify-between gap-3 bg-muted/50 rounded-lg p-3"
                  >
                    <button
                      className="text-left flex-1 min-w-0"
                      onClick={() => loadView(view)}
                      data-testid={`load-view-${view.view_id}`}
                    >
                      <p className="text-sm font-medium truncate">{view.view_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {view.description || schema.collections[view.primary_collection]?.label || view.primary_collection}
                      </p>
                    </button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteView(view.view_id)}
                      data-testid={`delete-view-${view.view_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
